/**
 * capsLogic.js
 * 
 * Модуль для генерации заглушек и переходов концентрических
 */

import fittingsData from '../fittingsData';

let idCounter = 0;
const generateUniqueId = () => {
  idCounter += 1;
  return `${Date.now()}-${idCounter}`;
};

/**
 * Получить ключ заглушки по типу и диаметру/профилю
 */
export const getCapKey = (capType, diameter, isSquare = false, profile = null) => {
  if (isSquare) {
    // Для квадратных труб всегда прямые заглушки с профилем
    // Заменяем латинскую x на русскую х для соответствия ключам в каталоге
    const profileKey = profile.replace('x', 'х');
    return `Zaglushka_PR_${profileKey}`;
  }
  
  if (capType === 'straight') {
    return `Zaglushka_PR_${diameter}`;
  } else {
    return `Zaglushka_EL_${diameter}`;
  }
};

/**
 * Генерация заглушки
 * 
 * @param {object} params - параметры
 * @param {string} params.capType - тип заглушки ('straight' или 'elliptical')
 * @param {number} params.diameter - диаметр трубы (для круглых)
 * @param {string} params.shape - форма трубы ('round' или 'square')
 * @param {string} params.profile - профиль трубы (для квадратных, например '80x80')
 * @param {number} params.x - позиция X (groupX)
 * @param {number} params.y - позиция Y (groupY)
 * @param {number} params.rotation - поворот (90 для правой, -90 для левой)
 * @param {string} params.side - сторона ('left' или 'right')
 * @returns {object|null} - объект фитинга или null
 */
export const generateCap = (params) => {
  const { capType, diameter, shape = 'round', profile, x, y, rotation, side } = params;
  
  const isSquare = shape === 'square';
  const capKey = getCapKey(capType, diameter, isSquare, profile);
  const capData = fittingsData[capKey];
  
  if (!capData) return null;
  
  const capId = `fitting-cap-${side}-${generateUniqueId()}`;
  
  return {
    id: capId,
    key: capKey,
    itemKey: capKey,
    x: 0,
    y: 0,
    width: capData.diameter,
    height: capData.length,
    groupX: x,
    groupY: y,
    rotation: rotation,
    offsetX: capData.diameter / 2,
    offsetY: capData.length,
  };
};

/**
 * Генерация перехода концентрического
 * 
 * @param {object} params - параметры
 * @param {string} params.reducerKey - ключ перехода
 * @param {number} params.x - позиция X (groupX)
 * @param {number} params.y - позиция Y (groupY)
 * @param {number} params.rotation - поворот (90 для правой, -90 для левой)
 * @param {string} params.side - сторона ('left' или 'right')
 * @returns {object|null} - объект фитинга или null
 */
export const generateReducer = (params) => {
  const { reducerKey, x, y, rotation, side } = params;
  
  const reducerData = fittingsData[reducerKey];
  
  if (!reducerData) return null;
  
  const reducerId = `fitting-reducer-${side}-${generateUniqueId()}`;
  
  return {
    id: reducerId,
    key: reducerKey,
    itemKey: reducerKey,
    x: 0,
    y: 0,
    width: reducerData.diameter,
    height: reducerData.length,
    groupX: x,
    groupY: y,
    rotation: rotation,
    offsetX: reducerData.diameter / 2,
    offsetY: reducerData.length,
  };
};

/**
 * Получить длину заглушки или перехода
 * 
 * @param {object} params - параметры
 * @param {boolean} params.hasCap - есть ли заглушка/переход
 * @param {string} params.capElementType - тип элемента ('cap' или 'reducer')
 * @param {string} params.capType - тип заглушки ('straight' или 'elliptical')
 * @param {string} params.reducerKey - ключ перехода
 * @param {number} params.diameter - диаметр трубы
 * @param {string} params.shape - форма трубы ('round' или 'square')
 * @param {string} params.profile - профиль трубы (для квадратных)
 * @returns {object} - { actualLength, straightLength }
 */
export const getCapLength = (params) => {
  const { hasCap, capElementType, capType, reducerKey, diameter, shape = 'round', profile } = params;
  
  let actualLength = 0;
  let straightLength = 0;
  
  if (!hasCap) {
    return { actualLength, straightLength };
  }
  
  if (capElementType === 'reducer') {
    // Переход концентрический
    const reducerData = fittingsData[reducerKey];
    if (reducerData) {
      actualLength = reducerData.length;
      // Переходы НЕ входят в общий размер гребёнки (как эллиптические заглушки)
    }
  } else {
    // Заглушка
    const isSquare = shape === 'square';
    const capKey = getCapKey(capType, diameter, isSquare, profile);
    const capData = fittingsData[capKey];
    
    if (capData) {
      actualLength = capData.length;
      if (capType === 'straight') {
        straightLength = capData.length;
      }
    }
  }
  
  return { actualLength, straightLength };
};

